import * as React from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    assignBattleAgentBot,
    getBattleAgentBotAccounts,
    getBattleAgents,
    removeBattleAgentBotFromAgent,
    updateBattleAgentBotBalance,
    updateBattleAgentBotProfile,
} from '../Api';
import type { BattleAgentBotAccountDto, BattleAgentDto } from '../models/dtos/BattleAgentDto';

export default function BattleAgentsBotsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [message, setMessage] = React.useState<string | null>(null);
    const [items, setItems] = React.useState<BattleAgentBotAccountDto[]>([]);
    const [lowBalanceCount, setLowBalanceCount] = React.useState(0);
    const [agents, setAgents] = React.useState<BattleAgentDto[]>([]);

    const [editBot, setEditBot] = React.useState<BattleAgentBotAccountDto | null>(null);
    const [name, setName] = React.useState('');
    const [photoUrl, setPhotoUrl] = React.useState('');
    const [countryIso, setCountryIso] = React.useState('');
    const [balance, setBalance] = React.useState(0);
    const [saving, setSaving] = React.useState(false);

    const [assignUserId, setAssignUserId] = React.useState('');
    const [assignAgentId, setAssignAgentId] = React.useState('');

    const load = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        const [bots, agentList] = await Promise.all([getBattleAgentBotAccounts(), getBattleAgents()]);
        if (!bots) {
            setError('Failed to load bot accounts.');
            setItems([]);
        } else {
            setItems(bots.items);
            setLowBalanceCount(bots.lowBalanceCount);
        }
        setAgents(agentList ?? []);
        setLoading(false);
    }, []);

    React.useEffect(() => {
        load();
    }, [load]);

    const openEdit = (bot: BattleAgentBotAccountDto) => {
        setEditBot(bot);
        setName(bot.name ?? '');
        setPhotoUrl(bot.photoUrl ?? '');
        setCountryIso(bot.countryIso ?? '');
        setBalance(Math.round(bot.moneyBalance));
        setMessage(null);
    };

    const saveEdit = async () => {
        if (!editBot) return;
        setSaving(true);
        setMessage(null);
        const profile = await updateBattleAgentBotProfile(editBot.userId, {
            name: name.trim() || undefined,
            photoUrl: photoUrl.trim() || undefined,
            countryIso: countryIso.trim() || undefined,
        });
        if (!profile.ok) {
            setMessage(profile.error);
            setSaving(false);
            return;
        }
        const bal = await updateBattleAgentBotBalance(editBot.userId, Math.max(0, Math.floor(balance)));
        setSaving(false);
        if (!bal.ok) {
            setMessage(bal.error);
            return;
        }
        setEditBot(null);
        setMessage('Bot updated.');
        await load();
    };

    const assignBot = async () => {
        const userId = Number(assignUserId);
        const agentId = Number(assignAgentId);
        if (!Number.isFinite(userId) || userId <= 0 || !Number.isFinite(agentId) || agentId <= 0) {
            setError('Enter a valid user id and choose an agent.');
            return;
        }
        const result = await assignBattleAgentBot(userId, [agentId]);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        setAssignUserId('');
        setMessage(`User #${userId} assigned to agent.`);
        await load();
    };

    const removeFromAgent = async (userId: number, agentId: number) => {
        if (!window.confirm(`Remove bot #${userId} from this agent?`)) return;
        const result = await removeBattleAgentBotFromAgent(userId, agentId);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        await load();
    };

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
                    <Typography variant="h4">Battle agents — bot accounts</Typography>
                    <Stack direction="row" spacing={1}>
                        <Button onClick={() => navigate('/battle-agents')}>Agents</Button>
                        <Button onClick={() => navigate('/battle-agents/stats')}>Stats</Button>
                        <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
                    </Stack>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                    All creator user IDs configured on battle agents. Edit name / photo URL / country / balance here.
                    Low balance = balance below estimated weekly battle create spend (stake mid × maxPerDay × 7 for linked enabled agents).
                    Activity bets (occasional betlists) are toggled in global battle-agent settings.
                </Typography>

                {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
                {message && <Alert severity="success" onClose={() => setMessage(null)}>{message}</Alert>}
                {lowBalanceCount > 0 && (
                    <Alert severity="warning">{lowBalanceCount} bot(s) may not have enough betcoins for ~1 week of battle creates.</Alert>
                )}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                    <TextField
                        size="small"
                        label="Add user id as bot"
                        value={assignUserId}
                        onChange={e => setAssignUserId(e.target.value)}
                    />
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Assign to agent</InputLabel>
                        <Select
                            label="Assign to agent"
                            value={assignAgentId}
                            onChange={e => setAssignAgentId(String(e.target.value))}
                        >
                            {agents.map(a => (
                                <MenuItem key={a.id} value={a.id}>{a.slug}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="contained" onClick={assignBot}>Add to agent</Button>
                </Stack>

                {loading && <CircularProgress />}

                {!loading && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Bot</TableCell>
                                <TableCell>Country</TableCell>
                                <TableCell align="right">Balance</TableCell>
                                <TableCell>Runway</TableCell>
                                <TableCell>Agents</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map(bot => (
                                <TableRow key={bot.userId} sx={{ backgroundColor: bot.lowBalance ? '#fff8e1' : undefined }}>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar src={bot.photoUrl ?? undefined} sx={{ width: 32, height: 32 }} />
                                            <Box>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {bot.name ?? `User #${bot.userId}`}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    #{bot.userId}{bot.email ? ` · ${bot.email}` : ''}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{bot.countryName ?? bot.countryIso ?? '—'}</TableCell>
                                    <TableCell align="right">{Math.round(bot.moneyBalance).toLocaleString()}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            color={bot.lowBalance ? 'warning' : 'default'}
                                            label={`${bot.runwayLabel} · ~${bot.weeklyEstimatedSpend.toLocaleString()}/wk`}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                            {bot.agents.map(a => (
                                                <Chip
                                                    key={a.id}
                                                    size="small"
                                                    label={a.slug}
                                                    onDelete={() => removeFromAgent(bot.userId, a.id)}
                                                    onClick={() => navigate(`/battle-agents/${a.id}/edit`)}
                                                />
                                            ))}
                                            {bot.agents.length === 0 && '—'}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" onClick={() => openEdit(bot)}>Edit</Button>
                                            <Button size="small" onClick={() => navigate(`/users/${bot.userId}`)}>User page</Button>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {items.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Typography color="text.secondary">
                                            No bots yet. Add a user id to an agent above, or set creators on an agent edit page.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Stack>

            <Dialog open={editBot != null} onClose={() => setEditBot(null)} fullWidth maxWidth="sm">
                <DialogTitle>Edit bot #{editBot?.userId}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" value={name} onChange={e => setName(e.target.value)} fullWidth />
                        <TextField label="Photo URL" value={photoUrl} onChange={e => setPhotoUrl(e.target.value)} fullWidth />
                        <TextField
                            label="Country ISO"
                            value={countryIso}
                            onChange={e => setCountryIso(e.target.value.toUpperCase())}
                            helperText="2-letter ISO, e.g. RO, MD, US"
                            fullWidth
                        />
                        <TextField
                            type="number"
                            label="Balance (absolute)"
                            value={balance}
                            onChange={e => setBalance(Number(e.target.value))}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditBot(null)}>Cancel</Button>
                    <Button variant="contained" onClick={saveEdit} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
